/**
 * VocabularyList.jsx
 * ------------------
 * Reusable vocabulary renderer for the Skills track (and any future track).
 *
 * Accepts a `items` array in either of two shapes for backward compatibility:
 *  1) string[]                — legacy simple chip mode (Balagha & Hamza tracks).
 *  2) VocabItem[]             — new rich schema for the Skills track:
 *     {
 *       word: string           // Arabic term with tashkil (required)
 *       meaning_en?: string    // English translation for AFL learners
 *       example?: string       // sample sentence using the term
 *       pronunciation?: string // optional IPA / transliteration (e.g. "al-inṣāt")
 *     }
 *
 * Rich items render as expandable cards (click → reveals meaning/example).
 * Strings render as compact chips (identical to the pre-existing UI).
 * Both modes can coexist in one list — the component decides per-item.
 */
import { useState } from "react";
import { ChevronDown } from "lucide-react";

const isRich = (v) => v && typeof v === "object" && "word" in v;

function VocabCard({ item, index }) {
  const [open, setOpen] = useState(false);
  const hasDetails = item.meaning_en || item.example || item.pronunciation;
  return (
    <div
      className={`vocab-card ${open ? "is-open" : ""}`}
      data-testid={`vocab-card-${index}`}
    >
      <button
        type="button"
        className="vocab-card-head"
        onClick={() => hasDetails && setOpen((o) => !o)}
        aria-expanded={open}
        disabled={!hasDetails}
        data-testid={`vocab-card-toggle-${index}`}
      >
        <span className="vocab-card-word">{item.word}</span>
        {item.pronunciation && (
          <span className="vocab-card-pron">/{item.pronunciation}/</span>
        )}
        {hasDetails && (
          <ChevronDown
            size={18}
            className="vocab-card-chevron"
            aria-hidden="true"
          />
        )}
      </button>
      {open && hasDetails && (
        <div className="vocab-card-body" data-testid={`vocab-card-body-${index}`}>
          {item.meaning_en && (
            <div className="vocab-card-row">
              <span className="vocab-card-label">Meaning</span>
              <span className="vocab-card-value" dir="ltr">
                {item.meaning_en}
              </span>
            </div>
          )}
          {item.example && (
            <div className="vocab-card-row">
              <span className="vocab-card-label">مثال</span>
              <span className="vocab-card-value">{item.example}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function VocabularyList({ items = [] }) {
  if (!items.length) return null;
  const allSimple = items.every((v) => !isRich(v));

  // Legacy path: all-string list → keep the old compact chip row so
  // Balagha/Hamza tracks look identical to before.
  if (allSimple) {
    return (
      <div className="vocab" data-testid="vocab-list">
        {items.map((w, i) => (
          <span className="chip" key={i} data-testid={`vocab-chip-${i}`}>
            {w}
          </span>
        ))}
      </div>
    );
  }

  // Rich path: render cards; fall back to simple chip for any string entries.
  return (
    <div className="vocab-cards" data-testid="vocab-list">
      {items.map((it, i) =>
        isRich(it) ? (
          <VocabCard item={it} index={i} key={i} />
        ) : (
          <span className="chip" key={i} data-testid={`vocab-chip-${i}`}>
            {it}
          </span>
        )
      )}
    </div>
  );
}
